import { FocusEventHandler, useEffect, useRef } from 'react'

import { useLatestCallback } from '@/hooks/useLatestCallback'
import { chunk } from '@/utils/array'

interface LicenseCodeProps {
  codeLength: number
  perGroup: number
  defaultValue?: string
  placeholder?: string
  containerClassName?: string
  inputClassName?: string
  disabled?: boolean
  onBlur?: () => void
  onCodeChange: (code: string) => void
}

export function LicenseCode({
  codeLength,
  perGroup,
  placeholder,
  containerClassName,
  inputClassName,
  disabled,
  onBlur,
  onCodeChange,
  defaultValue = '',
}: LicenseCodeProps): JSX.Element {
  const groupCount = Math.ceil(codeLength / perGroup)
  const inputsRef = useRef<HTMLInputElement[] | null[]>(Array(groupCount).fill(null))
  const isFocused = useRef(false)
  const isPasting = useRef(false)

  const isComposing = useRef(false)

  const handleSingleCodeChange = (index: number, value: string, recur = false) => {
    if (isPasting.current || isComposing.current) return

    const currentInput = inputsRef.current[index]
    const nextInput = inputsRef.current[index + 1]

    currentInput && (currentInput.value = value.slice(0, perGroup))
    if (value.length >= perGroup) {
      if (nextInput) {
        nextInput.focus()
        const remain = value.slice(perGroup)
        if (remain) {
          handleSingleCodeChange(index + 1, remain, true)
        } else nextInput.select()
      }
    }

    if (!recur) onCodeChange(inputsRef.current.map((input) => input?.value ?? '').join(''))
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    const prevInput = inputsRef.current[index - 1]
    const nextInput = inputsRef.current[index + 1]
    switch (event.key) {
      case 'Backspace':
        if (!event.currentTarget.value) {
          if (prevInput) {
            prevInput.focus()
            const prevInputLength = prevInput.value.length
            prevInput.setSelectionRange(prevInputLength, prevInputLength)
          }
        }

        break
      case 'ArrowLeft':
        if (!prevInput) return
        event.preventDefault()
        prevInput.focus()

        break
      case 'ArrowRight':
        if (!nextInput) return
        event.preventDefault()
        nextInput.focus()

        break
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault()
        break
    }
  }

  const handleFocus: FocusEventHandler<HTMLInputElement> | undefined = (event) => {
    event.currentTarget.select()
    isFocused.current = true
  }

  const handleBlur: FocusEventHandler<HTMLInputElement> | undefined = () => {
    onBlur?.()
    isFocused.current = false
  }

  const onCodeChangeLatest = useLatestCallback(onCodeChange)

  useEffect(() => {
    function guardIsPasting(f: () => void) {
      isPasting.current = true
      try {
        f()
      } finally {
        isPasting.current = false
      }
    }

    const handlePaste = (event: ClipboardEvent) => {
      if (!isFocused.current) return

      guardIsPasting(() => {
        const clipboardData = event.clipboardData?.getData('text')
        if (!clipboardData) return

        // select first input
        inputsRef.current[0]?.focus()
        event.preventDefault()

        const code = clipboardData.toUpperCase().slice(0, codeLength)

        const groups = chunk(code, perGroup)

        inputsRef.current.forEach((input, i) => void (input && (input.value = groups[i] ?? '')))

        onCodeChangeLatest(code)
      })
    }

    window.addEventListener('paste', handlePaste)

    return () => {
      window.removeEventListener('paste', handlePaste)
    }
  }, [onCodeChangeLatest, perGroup, codeLength])

  const defaultValues = chunk(defaultValue, perGroup)

  return (
    <>
      <div className={containerClassName}>
        {Array(groupCount)
          .fill(null)
          .map((_, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              placeholder={placeholder}
              defaultValue={defaultValues[index]}
              onChange={(event) => {
                handleSingleCodeChange(index, event.target.value.toUpperCase())
              }}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onBlur={handleBlur}
              onFocus={handleFocus}
              className={inputClassName}
              disabled={disabled}
              onCompositionStart={() => void (isComposing.current = true)}
              onCompositionEnd={(event) => {
                isComposing.current = false
                handleSingleCodeChange(index, event.currentTarget.value.toUpperCase())
              }}
            />
          ))}
      </div>
    </>
  )
}
